import { User } from "../../../domain/models/User";
import { GenderHelper } from "../../../domain/models/Gender";
import { MessageToUserHelper } from "../../../domain/models/MessageToUser";

export class UserConverter {
  /**
   * Converts Firebase Realtime Database snapshot to User domain model.
   * @param data The Firebase snapshot data
   * @param identifier The user identifier (UID)
   * @returns User object or null if data is invalid
   */
  static fromFirebase(
    data: Record<string, any> | null | undefined,
    identifier: string,
  ): User | null {
    if (!data) {
      return null;
    }

    // Parse partnership identifiers (stored as maps in Firebase)
    const validPartnershipsIdentifiers: string[] = [];
    if (data.partnerships && typeof data.partnerships === "object") {
      const partnerships = data.partnerships as Record<string, unknown>;
      const addPartnershipKeys = (value: unknown) => {
        if (value && typeof value === "object") {
          validPartnershipsIdentifiers.push(
            ...Object.keys(value as Record<string, unknown>),
          );
        }
      };

      addPartnershipKeys(partnerships.active);
      addPartnershipKeys(partnerships.pending);
      addPartnershipKeys(partnerships.currentlyPlaying);
      addPartnershipKeys(partnerships.selected);
      addPartnershipKeys(partnerships.playingAlone);
      addPartnershipKeys(partnerships.character);
    }

    const uniquePartnershipsIdentifiers = Array.from(
      new Set(validPartnershipsIdentifiers),
    );

    // Parse requesting partners
    const requestingPartnersIdentifiers: Record<string, number> = {};
    if (data.requests && typeof data.requests === "object") {
      Object.entries(data.requests).forEach(([key, value]) => {
        if (typeof value === "number") {
          requestingPartnersIdentifiers[key] = value;
        }
      });
    }

    const lastPartnershipsVisits: Record<string, number> = {};
    if (
      data.lastPartnershipsVisits &&
      typeof data.lastPartnershipsVisits === "object"
    ) {
      Object.entries(data.lastPartnershipsVisits).forEach(([key, value]) => {
        if (typeof value === "number") {
          lastPartnershipsVisits[key] = value;
        }
      });
    }

    const partnershipsLastMatchesScreenVisit: Record<string, number> = {};
    if (
      data.partnershipsLastMatchesScreenVisit &&
      typeof data.partnershipsLastMatchesScreenVisit === "object"
    ) {
      Object.entries(data.partnershipsLastMatchesScreenVisit).forEach(
        ([key, value]) => {
          if (typeof value === "number") {
            partnershipsLastMatchesScreenVisit[key] = value;
          }
        },
      );
    }

    const partnershipsDeckTopCardIdentifiers: Record<string, string> = {};
    if (
      data.partnershipsDeckTopCardIdentifiers &&
      typeof data.partnershipsDeckTopCardIdentifiers === "object"
    ) {
      Object.entries(data.partnershipsDeckTopCardIdentifiers).forEach(
        ([key, value]) => {
          if (typeof value === "string") {
            partnershipsDeckTopCardIdentifiers[key] = value;
          }
        },
      );
    }

    const partnershipsNumberOfCardsInDeckLeft: Record<string, number> = {};
    if (
      data.partnershipsNumberOfCardsInDeckLeft &&
      typeof data.partnershipsNumberOfCardsInDeckLeft === "object"
    ) {
      Object.entries(data.partnershipsNumberOfCardsInDeckLeft).forEach(
        ([key, value]) => {
          if (typeof value === "number") {
            partnershipsNumberOfCardsInDeckLeft[key] = value;
          }
        },
      );
    }

    return {
      identifier,
      fullName: data.fullName || "",
      email: data.email || "",
      profilePhotoURL: data.profilePhotoURL,
      gender: GenderHelper.fromFirebaseValue(data.gender),
      didPassTutorialCards: data.didPassTutorialCards ?? true,
      didFinishOnboarding: data.didFinishOnboarding ?? true,
      lastOpenedVersionOfApp: data.lastOpenedVersionOfApp,
      invitationCode: data.invitationCode,
      messageToUser:
        MessageToUserHelper.fromFirebaseValue(data.messageToUser) ?? undefined,
      isPlayingAlone: data.isPlayingAlone ?? false,
      didSeeExtraSwipesQueuedExplainer:
        data.didSeeExtraSwipesQueuedExplainer === true,
      selectedCharacter:
        typeof data.selectedCharacter === "string"
          ? data.selectedCharacter
          : null,
      validPartnershipsIdentifiers: uniquePartnershipsIdentifiers,
      requestingPartnersIdentifiers,
      lastPartnershipsVisits,
      partnershipsLastMatchesScreenVisit,
      partnershipsDeckTopCardIdentifiers,
      partnershipsNumberOfCardsInDeckLeft,
    };
  }

  /**
   * Converts User domain model to Firebase Realtime Database update format.
   * Returns a flat object with full paths as keys.
   * @param user The User object to convert
   * @returns Firebase update object with full paths
   */
  static toFirebase(user: User): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const basePath = `users/${user.identifier}/profile`;

    updates[`${basePath}/fullName`] = user.fullName;
    updates[`${basePath}/email`] = user.email;
    updates[`${basePath}/gender`] = GenderHelper.toFirebaseValue(user.gender);
    updates[`${basePath}/didPassTutorialCards`] = user.didPassTutorialCards;
    updates[`${basePath}/didFinishOnboarding`] = user.didFinishOnboarding;

    if (user.profilePhotoURL) {
      updates[`${basePath}/profilePhotoURL`] = user.profilePhotoURL;
    }

    if (user.lastOpenedVersionOfApp) {
      updates[`${basePath}/lastOpenedVersionOfApp`] =
        user.lastOpenedVersionOfApp;
    }

    if (user.invitationCode) {
      updates[`${basePath}/invitationCode`] = user.invitationCode;
    }

    if (user.messageToUser) {
      updates[`${basePath}/messageToUser`] =
        MessageToUserHelper.toFirebaseValue(user.messageToUser);
    }

    // Partnerships are stored as maps in Firebase. Avoid writing status maps
    // without explicit status data available in the domain model.

    updates[`users/${user.identifier}/profile/isPlayingAlone`] =
      user.isPlayingAlone;

    return updates;
  }

  /**
   * Converts partial User updates to Firebase format.
   * Only includes non-undefined fields.
   * @param identifier The user identifier
   * @param updates Partial user updates
   * @returns Firebase update object with full paths
   */
  static partialToFirebase(
    identifier: string,
    updates: Partial<User>,
  ): Record<string, unknown> {
    const firebaseUpdates: Record<string, unknown> = {};
    const basePath = `users/${identifier}/profile`;

    if (updates.fullName !== undefined) {
      firebaseUpdates[`${basePath}/fullName`] = updates.fullName;
    }

    if (updates.email !== undefined) {
      firebaseUpdates[`${basePath}/email`] = updates.email;
    }

    if (updates.gender !== undefined) {
      firebaseUpdates[`${basePath}/gender`] = GenderHelper.toFirebaseValue(
        updates.gender,
      );
    }

    if (updates.profilePhotoURL !== undefined) {
      firebaseUpdates[`${basePath}/profilePhotoURL`] = updates.profilePhotoURL;
    }

    if (updates.didPassTutorialCards !== undefined) {
      firebaseUpdates[`${basePath}/didPassTutorialCards`] =
        updates.didPassTutorialCards;
    }

    if (updates.didFinishOnboarding !== undefined) {
      firebaseUpdates[`${basePath}/didFinishOnboarding`] =
        updates.didFinishOnboarding;
    }

    if (updates.lastOpenedVersionOfApp !== undefined) {
      firebaseUpdates[`${basePath}/lastOpenedVersionOfApp`] =
        updates.lastOpenedVersionOfApp;
    }

    if (updates.invitationCode !== undefined) {
      firebaseUpdates[`${basePath}/invitationCode`] = updates.invitationCode;
    }

    if (updates.messageToUser !== undefined) {
      firebaseUpdates[`${basePath}/messageToUser`] =
        MessageToUserHelper.toFirebaseValue(updates.messageToUser);
    }

    if (updates.isPlayingAlone !== undefined) {
      firebaseUpdates[`${basePath}/isPlayingAlone`] = updates.isPlayingAlone;
    }

    if (updates.selectedCharacter !== undefined) {
      firebaseUpdates[`${basePath}/selectedCharacter`] =
        updates.selectedCharacter;
    }

    return firebaseUpdates;
  }
}
